import React, { ReactNode, PureComponent, Fragment } from 'react';
import { DB_TABLES } from '../../../../store/dbTables';
import { getTemplatesMeta } from '../../../../store/idbHandler';
import {
    INVESTIGATION,
    MEDICINES,
    DIAGNOSIS,
    SYMPTOMS,
    LAB_VITALS,
    EXAMINATIONS,
    INJECTIONS,
    PROCEDURES,
} from '../../../../strings';
import { getGenieTable } from '../workspace/UseTemplateDialog';
import Modal from '../../../common/Modal';
import WithBodyClickClose from '../../../common/WithBodyClickClose';
import { ALL_KINDS } from '../../../../utils/enums';
import { getMyDbId } from '../../../../api/mactiveid';

type Props = {
    onSave: (name: string, id?: string) => void;
    onCancel: () => void;
    gPTemplates: { id: string; name: string; isPackage?: boolean }[];
    title: string;
    createTemplateCss: string;
    isCancelActive: boolean;
    isForPackages?: boolean;
};

type State = {
    input: string;
    selectedTemplateId: string;
    isSaved?: boolean;
    selectedTab: 'NEW' | 'UPDATE';
};

const CreateTemplateDialog: React.FC<Props> = ({
    onCancel,
    onSave,
    gPTemplates,
    title,
    createTemplateCss,
    isCancelActive,
    isForPackages,
}) => {
    return (
        <Modal closeMe={onCancel} contentCssClasses={'h-50p'}>
            <CreateTemplate
                onCancel={onCancel}
                onSave={onSave}
                gPTemplates={gPTemplates}
                title={title}
                createTemplateCss={createTemplateCss}
                isCancelActive={isCancelActive}
                isForPackages={isForPackages}
            />
        </Modal>
    );
};

type CreatePxElementTemplateDialogState = {
    gPTemplates: { id: string; name: string }[];
};

export const TITLE_MAP: { [key in keyof typeof ALL_KINDS]: string } = {
    PX: 'Prescription as Template',
    LAB_TESTS_KIND: `${INVESTIGATION} as Template (Panel)`,
    MEDICINES_KIND: `${MEDICINES} as Template`,
    DIAGNOSIS_KIND: `${DIAGNOSIS} as Template`,
    SYMPTOMS_KIND: `${SYMPTOMS} as Template`,
    LAB_VITALS_KIND: `${LAB_VITALS} as Template`,
    EXAMINATIONS_KIND: `${EXAMINATIONS} as Template`,
    INJECTIONS_KIND: `${INJECTIONS} as Template`,
    PROCEDURES_KIND: `${PROCEDURES} as Template`,
};

type CreatePxElementTemplateDialogProps = {
    isModal: boolean;
    withPeak: boolean;
    kind: ALL_KINDS;
    onSave: (name: string, id?: string) => void;
    onCancel: () => void;
    isCancelActive: boolean;
    isForPackages?: boolean;
};

type WrapperProps = {
    closeMe: () => void;
    children: JSX.Element;
    withPeak: boolean;
};

class Wrapper extends PureComponent<WrapperProps> {
    render(): ReactNode {
        const { closeMe, children, withPeak } = this.props;

        return withPeak ? (
            <WithBodyClickClose closeMe={closeMe}>{children}</WithBodyClickClose>
        ) : (
            <Fragment>{children}</Fragment>
        );
    }
}

class CreateTemplateComp extends PureComponent<
    CreatePxElementTemplateDialogProps,
    CreatePxElementTemplateDialogState
> {
    state: CreatePxElementTemplateDialogState = {
        gPTemplates: [],
    };

    title = `Save ${TITLE_MAP[this.props.kind]}`;

    componentDidMount = async (): Promise<void> => {
        const table: DB_TABLES | undefined = getGenieTable(this.props.kind);
        if (!table) return;

        getTemplatesMeta(getMyDbId() || '', table).then((list) => {
            this.setState({
                gPTemplates: list,
            });
        });
    };

    render(): ReactNode {
        const { isModal, withPeak, onCancel, isCancelActive, isForPackages } = this.props;
        const Comp = isModal ? CreateTemplateDialog : CreateTemplate;
        const createTemplateCss = withPeak
            ? 'absolute z-10 -right-27 top-36 rounded-16 py-16 global-template-popup popup flex flex-col border border-neutral-7 w-320 lg:w-462'
            : 'flex z-10 rounded-16 py-16 flex flex-col h-full w-full w-462 bg-white';

        return (
            <Wrapper closeMe={onCancel} withPeak={withPeak}>
                <Comp
                    {...this.props}
                    gPTemplates={this.state.gPTemplates}
                    title={this.title}
                    createTemplateCss={createTemplateCss}
                    isCancelActive={isCancelActive}
                    isForPackages={isForPackages}
                />
            </Wrapper>
        );
    }
}

class CreateTemplate extends PureComponent<Props, State> {
    DEFAULT_ID = '0';
    state: State = {
        input: '',
        selectedTemplateId: this.DEFAULT_ID,
        selectedTab: 'NEW',
    };

    selectUpdate = (): void => {
        this.setState({
            selectedTab: 'UPDATE',
        });
    };

    selectNew = (): void => {
        this.setState({
            selectedTab: 'NEW',
        });
    };

    onSave = (): void => {
        if (this.isSaveBtDisabled()) {
            return;
        }

        const { input, selectedTemplateId } = this.state;
        const { gPTemplates } = this.props;

        if (input) {
            this.props.onSave(this.state.input);
            this.setState({
                isSaved: true,
            });
        } else if (selectedTemplateId) {
            const gT = gPTemplates.find((t) => t.id === selectedTemplateId);
            if (gT) {
                this.props.onSave(gT.name, selectedTemplateId);
                this.setState({
                    isSaved: true,
                });
            }
        }
    };

    onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const input = e.target.value;
        this.setState({
            input: input,
            selectedTemplateId: this.DEFAULT_ID,
            isSaved: false,
        });
    };

    onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            this.onSave();
        }
    };

    onTemplateSelectChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedTemplateId = e.target.value;
        this.setState({
            selectedTemplateId,
            input: '',
            isSaved: false,
        });
    };

    isSaveBtDisabled = (): boolean => {
        const { input, selectedTemplateId, selectedTab } = this.state;

        return selectedTab === 'NEW'
            ? !input || input.length === 0
            : selectedTemplateId === this.DEFAULT_ID;
    };

    render(): ReactNode {
        const { gPTemplates, createTemplateCss, isCancelActive, isForPackages } = this.props;
        const { input, selectedTemplateId, isSaved, selectedTab } = this.state;

        const isDisabled = this.isSaveBtDisabled();
        const options = isForPackages
            ? gPTemplates.filter((i) => i.isPackage)
            : gPTemplates.filter((i) => !i.isPackage);

        return (
            <div id="dialog" className={createTemplateCss}>
                <div className="pl-16 pr-24 h-full w-full flex items-center justify-end ml-8">
                    {isCancelActive && (
                        <div
                            className="flex items-center space-x-4 cursor-pointer"
                            onClick={() => this.props.onCancel()}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 320 512"
                                className="w-16 h-16 fill-neutral-3 hover:fill-neutral-1"
                            >
                                <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                            </svg>
                        </div>
                    )}
                </div>
                <div className="flex flex-col  items-center px-16 w-full">
                    <div className="w-full border-b border-neutral-8 select-none">
                        <div className="flex items-center font-600 text-14 leading-28 cursor-pointer ml-8">
                            <div
                                className={
                                    selectedTab === 'NEW'
                                        ? 'border-b-2 border-actionPrimary  text-13 font-700'
                                        : 'border-b-2 border-transparent text-13 font-700'
                                }
                                onClick={this.selectNew}
                            >
                                <p
                                    className={
                                        selectedTab === 'NEW'
                                            ? 'pb-8 text-actionPrimary text-13 font-700'
                                            : 'pb-8 text-neutral-3 text-13 font-700'
                                    }
                                >
                                    New {isForPackages ? 'Package' : 'Template'}
                                </p>
                            </div>
                            <div
                                className={
                                    selectedTab === 'UPDATE'
                                        ? 'border-b-2 mx-16 border-actionPrimary  text-13 font-700'
                                        : 'border-b-2 mx-16 border-transparent text-13 font-700'
                                }
                                onClick={this.selectUpdate}
                            >
                                <p
                                    className={
                                        selectedTab === 'UPDATE'
                                            ? 'pb-8 text-actionPrimary text-13 font-700'
                                            : 'pb-8 text-neutral-3 text-13 font-700'
                                    }
                                >
                                    Update existing {isForPackages ? 'Package' : 'Template'}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex w-full mt-16 mb-8">
                        <div className="pr-12 flex flex-col w-full justify-between items-center">
                            <div className="w-full ">
                                {selectedTab === 'NEW' && (
                                    <div className="pt-1 bg-white ether-spaced-input-component text-neutral-5 border border-neutral-6">
                                        <div className="flex items-center w-full">
                                            <div className="flex z-0 w-full">
                                                <input
                                                    placeholder={`${
                                                        isForPackages ? 'Package' : 'Template'
                                                    } Name`}
                                                    value={input}
                                                    autoFocus
                                                    onChange={this.onInputChange}
                                                    onKeyDown={this.onKeyDown}
                                                    className="text-14 font-400"
                                                ></input>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {selectedTab === 'UPDATE' && (
                                    <div className="px-16 space-x-12 py-7 mt-1 overflow-auto border border-neutral-5 rounded-4 bg-white">
                                        <select
                                            value={selectedTemplateId}
                                            onChange={this.onTemplateSelectChange}
                                            className="form-control w-full text-neutral-black font-600 focus:outline-none"
                                        >
                                            <option value={this.DEFAULT_ID}>-</option>
                                            {options.map((t) => (
                                                <option key={t.id} value={t.id}>
                                                    {t.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => (!isSaved ? this.onSave() : undefined)}
                            className="primary-btn btn--small px-12 py-6"
                            disabled={isDisabled}
                        >
                            <p className="text-center w-full">
                                {selectedTab === 'NEW' ? 'Save' : 'Update'}
                            </p>

                            {isSaved && (
                                <div className="flex items-center justify-center ml-8">
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        className="fill-white"
                                    >
                                        <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"></path>
                                    </svg>
                                </div>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default CreateTemplateComp;
